import React, { useEffect, useState } from 'react'
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import FooterContact from '../../../component/FooterContact';
import HomeVideo2 from '../../../assets/images/banner/banner-1.mp4';

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } }
};

const howItWorks = [
    { step: '01', title: 'Deploy Decoys', desc: 'Realistic traps are deployed throughout your infrastructure - fake servers, credentials, files, and network nodes that mirror real assets.' },
    { step: '02', title: 'Attacker Engages', desc: 'When an intruder enters your network, they encounter the decoys. Unaware they are fake, attackers waste time and reveal their methods.' },
    { step: '03', title: 'Alert & Capture', desc: 'The moment a trap is triggered, your centralized deception server records attack vectors, captures TTPs, and sends real-time alerts.' },
    { step: '04', title: 'Analyze & Harden', desc: 'Intelligence gathered from the deception layer is used to improve defenses, patch vulnerabilities, and prevent future breaches.' }
];

const importanceItems = [
    { icon: '⏱️', title: 'Reduce Dwell Time', desc: 'Decrease the time attackers spend undetected in your network by luring them into dead-end traps immediately.' },
    { icon: '🎯', title: 'Early Detection', desc: 'Detect threats earlier in the attack chain - before critical assets are compromised or data is exfiltrated.' },
    { icon: '🚨', title: 'Fewer False Positives', desc: 'Deception alerts are nearly always true positives since legitimate users never interact with decoy assets.' },
    { icon: '📊', title: 'Rich Threat Intelligence', desc: 'Capture detailed IOCs, TTPs, and attacker behavior to continuously improve your security posture.' }
];

const benefits = [
    'Detecting discovery and lateral movement',
    'Privilege escalation and collection activities',
    'Prevent internal threat reconnaissance',
    'Man-in-the-middle attack prevention',
    'Protect an ever-evolving attack surface',
    'Real-time attacker intelligence capture'
];

export default function DeceptionTechnology() {

    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
        const interval = setInterval(() => {
            setActiveStep(prev => (prev + 1) % howItWorks.length);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <>
            {/* Hero Banner */}
            <section className="tech-hero-banner" style={{ position: 'relative', overflow: 'hidden' }}>
                <div className="tech-hero-overlay"></div>
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center" style={{ minHeight: '85vh' }}>
                        <div className="col-lg-7 col-md-12">
                            <motion.div
                                className="tech-hero-content"
                                initial="hidden"
                                animate="visible"
                                variants={staggerContainer}
                            >
                                <motion.div variants={fadeUp} className="tech-breadcrumb">
                                    <Link to="/">Home</Link>
                                    <span className="tech-breadcrumb-sep"> / </span>
                                    <span>Deception Technology</span>
                                </motion.div>
                                <motion.div variants={fadeUp} className="tech-badge">
                                    Active Defense Strategy
                                </motion.div>
                                <motion.h1 variants={fadeUp} className="tech-hero-title">
                                    Stay Ahead with<br />
                                    <span className="tech-hero-gradient">Deception Technology</span>
                                </motion.h1>
                                <motion.p variants={fadeUp} className="tech-hero-desc">
                                    Proactively detect and distract attackers by creating realistic traps. Protect critical assets, prevent data breaches, and gather real-time intelligence on threat actors.
                                </motion.p>
                                <motion.div variants={fadeUp} className="tech-hero-actions">
                                    <Link to="/contact" className="tech-btn-primary">Request Demo</Link>
                                    <Link to="/contact" className="tech-btn-ghost">Learn More</Link>
                                </motion.div>
                            </motion.div>
                        </div>
                        <div className="col-lg-5 col-md-12">
                            <motion.div
                                className="tech-hero-visual"
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.9, delay: 0.3 }}
                            >
                                <div className="deception-visual">
                                    <div className="deception-trap trap-1">
                                        <span>🪤</span>
                                        <small>Decoy</small>
                                    </div>
                                    <div className="deception-trap trap-2">
                                        <span>🪤</span>
                                        <small>Honeypot</small>
                                    </div>
                                    <div className="deception-trap trap-3">
                                        <span>🪤</span>
                                        <small>Fake Asset</small>
                                    </div>
                                    <div className="deception-attacker">
                                        <span>⚠️</span>
                                        <small>Attacker</small>
                                    </div>
                                    <div className="deception-center">
                                        <svg width="48" height="48" viewBox="0 0 50 50" fill="none">
                                            <path d="M25 2L4 12V26C4 37.1 13.3 47.5 25 50C36.7 47.5 46 37.1 46 26V12L25 2Z" fill="rgba(36,88,243,0.3)" stroke="#2458f3" strokeWidth="1.5" />
                                            <path d="M25 15V28M19 22L25 28L31 22" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <small>Protected</small>
                                    </div>
                                    <svg className="deception-lines" viewBox="0 0 200 200">
                                        <circle cx="100" cy="100" r="75" stroke="rgba(36,88,243,0.25)" strokeWidth="1" fill="none" strokeDasharray="6,4" />
                                    </svg>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <video autoPlay loop muted style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
                    <source src={HomeVideo2} type="video/mp4" />
                </video>
            </section>

            {/* What Is Deception Technology */}
            <section className="tech-section-light">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/services/technology/deception-tech-2.jpg')} alt='Deception Technology' />
                                <div className="tech-image-badge">
                                    <span className="badge-num">0</span>
                                    <span className="badge-label">False Positives</span>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag">Overview</div>
                            <h2 className="tech-section-heading">What Is <span className="tech-text-accent">Deception Technology?</span></h2>
                            <p className="tech-section-para">
                                Deception technology prevents cybercriminals who have infiltrated a network from doing significant damage. It generates traps and deception decoys that mimic legitimate technology assets throughout the infrastructure.
                            </p>
                            <p className="tech-section-para">
                                These decoys run in virtual or real operating system environments and trick the cybercriminal into thinking they found a way to escalate privileges or steal credentials - while the real assets remain completely safe.
                            </p>
                            <div className="tech-quote-block">
                                <p>"Once a trap is triggered, notifications are broadcast to a centralized deception server that records the affected decoy and the attack vectors used by cybercriminals."</p>
                            </div>
                            <Link to="/contact" className="tech-btn-primary mt-4 d-inline-block">Request a Demo</Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* How It Works - Steps */}
            <section className="tech-section-dark">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center mb-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag light">The Process</div>
                        <h2 className="tech-section-heading text-white">How Deception Technology <span className="tech-text-accent">Works</span></h2>
                        <p className="tech-section-para text-white" style={{ maxWidth: 600, margin: '0 auto', opacity: 0.85 }}>
                            Deception technology tricks attackers into engaging with fake resources, giving your team time to respond while protecting real assets.
                        </p>
                    </motion.div>
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {howItWorks.map((step, i) => (
                            <motion.div className="col-lg-3 col-md-6 mb-4" key={i} variants={fadeUp}>
                                <div
                                    className={`tech-how-step-card ${activeStep === i ? 'active' : ''}`}
                                    onClick={() => setActiveStep(i)}
                                >
                                    <div className="tech-how-step-num">{step.step}</div>
                                    <h3 className="tech-how-step-title">{step.title}</h3>
                                    <p className="tech-how-step-desc">{step.desc}</p>
                                    <div className="tech-how-step-bar"></div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Importance Section */}
            <section className="tech-section-light">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center mb-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag">Why It Matters</div>
                        <h2 className="tech-section-heading">Importance of <span className="tech-text-accent">Deception Technology</span></h2>
                        <p className="tech-section-para" style={{ maxWidth: 700, margin: '0 auto' }}>
                            As attack vectors become increasingly complex, organizations need to detect suspicious activity earlier in the attack chain. Deception technology gives security teams a critical early-warning system.
                        </p>
                    </motion.div>
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {importanceItems.map((item, i) => (
                            <motion.div className="col-lg-3 col-md-6 mb-4" key={i} variants={fadeUp}>
                                <div className="tech-pillar-card">
                                    <div className="tech-pillar-icon" style={{ fontSize: 36 }}>{item.icon}</div>
                                    <h3 className="tech-pillar-title">{item.title}</h3>
                                    <p className="tech-pillar-desc">{item.desc}</p>
                                    <div className="tech-pillar-line"></div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                    <motion.div
                        className="tech-info-callout mt-4"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <p>In incident detection and response, time and context are crucial. Many detection solutions wait until critical assets are compromised to send an alert - deception technology changes that by catching attackers in the act.</p>
                    </motion.div>
                </div>
            </section>

            {/* Stats */}
            <section className="tech-stats-section">
                <div className="container">
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {[
                            { stat: '~0', label: 'False Positive Alerts' },
                            { stat: '90%', label: 'Earlier Threat Detection' },
                            { stat: '3x', label: 'Faster Incident Response' },
                            { stat: '100%', label: 'Real-Time Attacker Capture' }
                        ].map((item, i) => (
                            <motion.div className="col-lg-3 col-md-6 mb-4" key={i} variants={fadeUp}>
                                <div className="tech-stat-card">
                                    <div className="tech-stat-num">{item.stat}</div>
                                    <div className="tech-stat-label">{item.label}</div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Benefits */}
            <section className="tech-section-gradient">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag light">Key Advantages</div>
                            <h2 className="tech-section-heading text-white">Deception Technology <span className="tech-text-accent">Benefits</span></h2>
                            <ul className="tech-benefits-list">
                                {benefits.map((benefit, i) => (
                                    <motion.li
                                        key={i}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.08 }}
                                    >
                                        <span className="tech-check-icon">✓</span>
                                        {benefit}
                                    </motion.li>
                                ))}
                            </ul>
                            <Link to="/contact" className="tech-btn-primary mt-4 d-inline-block">Get Started</Link>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/services/technology/benifit-detection.webp')} alt='Deception Benefits' />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <FooterContact />
        </>
    )
}
